import { IUserColor } from '../../user-info/IUserColor';

export interface CellColorContext {
    isReadOnly: boolean;
    isOutOfDateRange: boolean;
    hasValue: boolean;
    cellValue: any;
    dayData: any;
    userRole: 'user' | 'superUser' | 'supervisor' | 'taskManager';
    requireTimeSubmit: boolean;
    userWebColors: Array<IUserColor>;
    columnField?: string; // Optional field to identify column type (e.g., 'task')
}

export class CellColorManager {

    /**
     * Gets the appropriate cell colors based on cell state and user context
     */
    static getCellColors(context: CellColorContext): { backgroundColor: string, color: string, cssClass?: string } {
        const {
            isReadOnly,
            isOutOfDateRange,
            hasValue,
            cellValue,
            dayData,
            userRole,
            requireTimeSubmit,
            userWebColors
        } = context;

        // PRIORITY 1: Task Column Colors (always use PrimeNG row background, no exceptions)
        // Task column should have field 'task'
        const isTaskColumn = context.columnField === 'task' || context.dayData?.isTaskColumn === true;

        // Debug task column detection - log all column types to understand the structure
        console.log('Column detection debug:', {
            columnField: context.columnField,
            isTaskColumn,
            dayDataHasTaskFlag: context.dayData?.isTaskColumn,
            checkingForTask: context.columnField === 'task',
            columnType: context.columnField?.includes('EmployeeHours') ? 'hours' :
                       context.columnField === 'task' ? 'task' :
                       context.columnField === 'payrollItem' ? 'payroll' :
                       context.columnField === 'serviceItem' ? 'service' : 'other'
        });

        if (isTaskColumn) {
            console.log('Task column detected, using PrimeNG row background only:', {
                columnField: context.columnField
            });
            return {
                backgroundColor: 'var(--timecard-cell-bg-default)',
                color: 'var(--timecard-cell-color-default)',
                cssClass: isReadOnly || isOutOfDateRange ? 'timecard-cell-task-only disabledTimeCardCell' : 'timecard-cell-task-only' // Add disabled class if needed
            };
        }

        // PRIORITY 2: Data-driven Colors (for cells WITH values) - CHECK BEFORE DISABLED STYLING
        // Check for both possible property names (case sensitivity)
        const backColor = dayData?.Backcolor_hex || dayData?.backcolor_hex;
        const foreColor = dayData?.Forecolor_hex || dayData?.forecolor_hex;

        if ((hasValue || cellValue === 0) && backColor && foreColor) {
            // Check if the data colors are "default-like" (white/black that should be theme-reactive)
            const isDefaultLikeDataColor = (
                (backColor.toLowerCase() === '#ffffff' || backColor.toLowerCase() === '#fff') &&
                (foreColor.toLowerCase() === '#000000' || foreColor.toLowerCase() === '#000')
            ) || (
                (backColor.toLowerCase() === '#000000' || backColor.toLowerCase() === '#000') &&
                (foreColor.toLowerCase() === '#ffffff' || foreColor.toLowerCase() === '#fff')
            );

            if (isDefaultLikeDataColor) {
                console.log('Data colors are default-like, using theme-reactive approach:', { backColor, foreColor });
                return {
                    backgroundColor: 'var(--timecard-cell-bg-default)',
                    color: 'var(--timecard-cell-color-default)',
                    cssClass: isReadOnly || isOutOfDateRange ? 'disabledTimeCardCell' : undefined
                };
            } else {
                console.log('Using data-driven colors for cell with value:', { backColor, foreColor, isDisabled: isReadOnly || isOutOfDateRange });
                return {
                    backgroundColor: backColor,
                    color: foreColor,
                    cssClass: isReadOnly || isOutOfDateRange ? 'disabledTimeCardCell' : undefined // Apply disabled styling while preserving colors
                };
            }
        }

        // PRIORITY 3: Disabled/ReadOnly Colors (only for cells WITHOUT data colors)
        if (isReadOnly || isOutOfDateRange) {
            return {
                backgroundColor: 'var(--timecard-cell-bg-disabled)',
                color: 'var(--timecard-cell-color-disabled)',
                cssClass: 'disabledTimeCardCell'
            };
        }

        // PRIORITY 4: Default PrimeNG Colors (for cells WITHOUT values)
        // This ensures empty cells are theme-reactive
        if (!hasValue && cellValue !== 0 && !isReadOnly && !isOutOfDateRange) {
            console.log('Using default PrimeNG colors for empty cell');
            return {
                backgroundColor: 'var(--timecard-cell-bg-default)',
                color: 'var(--timecard-cell-color-default)'
            };
        }

        // Cells with a value but no Backcolor_hex/Forecolor_hex from the API (e.g. unsaved edit or before update response)
        // use default colors so that status colors (Saved/Signed) only appear after the update response supplies them.
        if (hasValue || cellValue === 0) {
            console.log('Cell has value but no API colors yet; using default until update response');
            return {
                backgroundColor: 'var(--timecard-cell-bg-default)',
                color: 'var(--timecard-cell-color-default)'
            };
        }

        // PRIORITY 6: Final Default Colors (absolute fallback)
        console.log('Using final default colors');
        return {
            backgroundColor: 'var(--timecard-cell-bg-default)',
            color: 'var(--timecard-cell-color-default)'
        };
    }

    /**
     * Gets CSS class name based on user role and submission requirements
     */
    private static getUserRoleCssClass(
        userRole: 'user' | 'superUser' | 'supervisor' | 'taskManager',
        requireTimeSubmit: boolean
    ): string {
        // Return a generic class name for dynamic coloring
        // The actual colors will come from inline styles based on API data
        if (userRole === 'superUser' || userRole === 'user') {
            return 'timecard-cell-role-based';
        }
        return '';
    }

    /**
     * Gets colors based on user role and submission requirements
     */
    private static getUserRoleColors(
        userRole: 'user' | 'superUser' | 'supervisor' | 'taskManager',
        requireTimeSubmit: boolean,
        userWebColors: Array<IUserColor>
    ): { backgroundColor: string, color: string } {

        // ALWAYS use userWebColors when available (they are user-defined from API)
        if (userWebColors && userWebColors.length > 0) {
            console.log('UserColors from API:', userWebColors.map(c => ({
                StatusID: c.StatusID,
                Status: c.Status,
                Background: c.Backcolor_hex,
                Foreground: c.Forecolor_Hex
            })));

            console.log('Color selection context:', {
                userRole,
                requireTimeSubmit,
                userWebColorsLength: userWebColors.length
            });

            // Use the original index-based approach that was working
            if (userRole === 'superUser') {
                // SuperUser colors: index 0 for background, different indices for foreground based on requireTimeSubmit
                const backColor = userWebColors[0]?.Backcolor_hex;
                const foreColor = requireTimeSubmit
                    ? userWebColors[1]?.Forecolor_Hex  // Signed state
                    : userWebColors[2]?.Backcolor_hex; // Saved state (using Backcolor as foreground)

                console.log('SuperUser color selection:', {
                    requireTimeSubmit,
                    backColor,
                    foreColor,
                    usingIndex: requireTimeSubmit ? 1 : 2
                });

                if (backColor && foreColor) {
                    return { backgroundColor: backColor, color: foreColor };
                }
            }

            if (userRole === 'user') {
                // User colors: different indices based on requireTimeSubmit
                const backColor = requireTimeSubmit
                    ? userWebColors[1]?.Backcolor_hex  // Signed state
                    : userWebColors[2]?.Backcolor_hex; // Saved state
                const foreColor = requireTimeSubmit
                    ? userWebColors[1]?.Forecolor_Hex  // Signed state
                    : userWebColors[2]?.Forecolor_Hex; // Saved state

                console.log('User color selection:', {
                    requireTimeSubmit,
                    backColor,
                    foreColor,
                    usingIndex: requireTimeSubmit ? 1 : 2
                });

                if (backColor && foreColor) {
                    return { backgroundColor: backColor, color: foreColor };
                }
            }

            // Fallback to first available color if specific indices not found
            const firstColor = userWebColors[0];
            if (firstColor?.Backcolor_hex && firstColor?.Forecolor_Hex) {
                console.log('Using fallback first color:', firstColor);
                return {
                    backgroundColor: firstColor.Backcolor_hex,
                    color: firstColor.Forecolor_Hex
                };
            }
        }

        // Final fallback to default colors when no userWebColors available
        console.warn('No userWebColors available, falling back to default colors. This may indicate an API issue.');
        return {
            backgroundColor: 'var(--timecard-cell-bg-default)',
            color: 'var(--timecard-cell-color-default)'
        };
    }

    /**
     * Applies colors to a Tabulator cell element
     */
    static applyCellColors(cell: any, colors: { backgroundColor: string, color: string, cssClass?: string }): void {
        if (!cell?.getElement) return;

        const element = cell.getElement();

        // Remove any existing color classes first
        element.classList.remove(
            'disabledTimeCardCell',
            'timecard-cell-role-based',
            'timecard-cell-default',
            'timecard-cell-primeng-default',
            'timecard-cell-task-only',
            'timecard-cell-user-signed',
            'timecard-cell-user-saved',
            'timecard-cell-superuser-signed',
            'timecard-cell-superuser-saved'
        );

        // Clear any existing inline background/color styles to let CSS take over
        element.style.removeProperty('background-color');
        element.style.removeProperty('color');

        // Debug what we're applying
        console.log('Applying colors to cell:', {
            backgroundColor: colors.backgroundColor,
            color: colors.color,
            cssClass: colors.cssClass,
            backgroundColorType: typeof colors.backgroundColor,
            colorType: typeof colors.color,
            backgroundColorLength: colors.backgroundColor?.length,
            colorLength: colors.color?.length,
            backgroundColorLower: colors.backgroundColor?.toLowerCase(),
            colorLower: colors.color?.toLowerCase()
        });

        // Apply CSS class if provided (this handles disabled cells)
        if (colors.cssClass) {
            element.classList.add(colors.cssClass);
        }

        // Check if this is a CSS variable (theme-reactive) or hex color (data-driven)
        const isCssVariable = colors.backgroundColor.startsWith('var(');

        if (isCssVariable) {
            // Special handling for task columns
            if (colors.cssClass === 'timecard-cell-task-only') {
                element.classList.add('timecard-cell-task-only');
                // Don't use inline styles for task columns - let CSS handle it completely
                console.log('Applied task-only CSS class for theme reactivity');
            } else if (colors.backgroundColor === 'var(--timecard-cell-bg-default)') {
                // Try the direct PrimeNG approach first
                element.classList.add('timecard-cell-primeng-default');
                console.log('Applied direct PrimeNG datatable color class for theme reactivity');
            } else {
                // Role-based CSS variables - add appropriate class based on the variable
                if (colors.backgroundColor.includes('--timecard-cell-bg-user-signed')) {
                    element.classList.add('timecard-cell-user-signed');
                } else if (colors.backgroundColor.includes('--timecard-cell-bg-user-saved')) {
                    element.classList.add('timecard-cell-user-saved');
                } else if (colors.backgroundColor.includes('--timecard-cell-bg-superuser-signed')) {
                    element.classList.add('timecard-cell-superuser-signed');
                } else if (colors.backgroundColor.includes('--timecard-cell-bg-superuser-saved')) {
                    element.classList.add('timecard-cell-superuser-saved');
                } else {
                    // Fallback - use inline styles for unknown CSS variables
                    element.style.setProperty('background-color', colors.backgroundColor, 'important');
                    element.style.setProperty('color', colors.color, 'important');
                }
                console.log('Applied role-based CSS class');
            }
        } else {
            // Hex colors from data - use inline styles
            element.style.setProperty('background-color', colors.backgroundColor, 'important');
            element.style.setProperty('color', colors.color, 'important');
            console.log('Applied data-driven hex colors:', colors.backgroundColor, colors.color);
        }
    }

    static applyDisabledCellStyle(cell: any): void {
        if (!cell?.getElement) return;

        const element = cell.getElement();
        element.classList.add('disabledTimeCardCell');
    }

    static createCellFormatter(
        tc: any, // TimeCardPresenterComponent instance
        getCellContext: (cell: any, data: any, day: string) => Partial<CellColorContext>
    ) {
        return function(cell: any) {
            const data = cell.getData();
            const column = cell.getColumn();
            const columnDef = column.getDefinition();
            const columnField = columnDef.field.split('.')[0];
            const day = columnField;

            // Handle special column types
            if (['serviceItem', 'payrollItem', 'task'].includes(cell._cell.column.field)) {
                cell.getElement().classList.add('default-cursor');
            }

            const value = cell.getValue();
            if (value === '') {
                cell.setValue(null, true);
            }

            // Build color context
            const baseContext: CellColorContext = {
                isReadOnly: data[day]?.IsReadOnly === true,
                isOutOfDateRange: data[day]?.OutOfDateRange === true,
                hasValue: !!(value || value === 0),
                cellValue: value,
                dayData: data[day],
                userRole: tc.isSuperUser ? 'superUser' :
                         tc.isSupervisor ? 'supervisor' :
                         tc.isTaskManager ? 'taskManager' : 'user',
                requireTimeSubmit: tc.requireTimeSubmit,
                userWebColors: tc.userWebColors,
                columnField: cell._cell.column.field // Add column field for task column detection
            };

            console.log(`Cell formatter debug for ${day}:`, {
                columnField: cell._cell.column.field,
                value,
                hasValue: baseContext.hasValue,
                isReadOnly: baseContext.isReadOnly,
                isOutOfDateRange: baseContext.isOutOfDateRange,
                userRole: baseContext.userRole,
                requireTimeSubmit: baseContext.requireTimeSubmit,
                dayData: data[day]
            });

            // Allow custom context overrides
            const context = { ...baseContext, ...getCellContext(cell, data, day) };

            // Apply colors using centralized logic
            const colors = CellColorManager.getCellColors(context);
            CellColorManager.applyCellColors(cell, colors);

            return value;
        };
    }
}
